import { cn } from '@/lib/utils'
import { CheckCircle2, XCircle, Clock, AlertCircle, Loader2 } from 'lucide-react'

type StatusType = 'success' | 'error' | 'pending' | 'warning' | 'loading'

interface StatusBadgeProps {
  status: StatusType
  label?: string
  size?: 'sm' | 'md'
}

const statusConfig: Record<
  StatusType,
  {
    icon: typeof CheckCircle2
    className: string
    defaultLabel: string
  }
> = {
  success: {
    icon: CheckCircle2,
    className: 'bg-success/10 text-success border-success/20',
    defaultLabel: 'Success',
  },
  error: {
    icon: XCircle,
    className: 'bg-destructive/10 text-destructive border-destructive/20',
    defaultLabel: 'Error',
  },
  pending: {
    icon: Clock,
    className: 'bg-muted text-muted-foreground border-border',
    defaultLabel: 'Pending',
  },
  warning: {
    icon: AlertCircle,
    className: 'bg-warning/10 text-warning border-warning/20',
    defaultLabel: 'Warning',
  },
  loading: {
    icon: Loader2,
    className: 'bg-info/10 text-info border-info/20',
    defaultLabel: 'Loading',
  },
}

export function StatusBadge({ status, label, size = 'md' }: StatusBadgeProps) {
  const config = statusConfig[status]
  const Icon = config.icon
  const displayLabel = label || config.defaultLabel

  const sizeStyles = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    md: 'text-xs px-2 py-1 gap-1.5',
  }

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        config.className,
        sizeStyles[size],
      )}
    >
      <Icon className={cn(iconSizes[size], status === 'loading' && 'animate-spin')} />
      {displayLabel}
    </span>
  )
}
